import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core'
import { getProperty } from '../../functions/getProperty'
import { doesPassNumeric } from '../../functions/doesPassNumeric'
import { doesPass } from '../../functions/doesPass'
import { every, some } from 'underscore'
import { takeUntil, debounceTime, take } from 'rxjs/operators'
import { fadeIn } from '../../animations/fadeIn'
import { MatPaginator } from '@angular/material/paginator'
import { updateProperty } from '../../functions/updateProperty'
import { BehaviorSubject, Subject } from 'rxjs'
import { KeySystem } from '../../models/dev.test.models'
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component'
import { DialogService } from '../../services/dialog.service'
import { LoadingService } from '../../services/loading.service'
import { ErrorService } from '../../services/error.service'
import { KeyingSystemService } from '../../services/keying.system.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { KeyingStructureService } from '../../services/keying.structure.service'
import { ChangeLogConfirmationDialogComponent } from '../matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { ToastService } from '@overtur-monorepo/keying-shared'

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    animations: [fadeIn],
    standalone: false
})
export class DataTableComponent implements OnInit, OnDestroy {
  @ViewChildren('tableHeader') tableHeaders: QueryList<ElementRef>
  @ViewChild('table', { static: true }) table: ElementRef
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChildren('searchInput') searchInputs: QueryList<ElementRef>

  @Input() subject: BehaviorSubject<Array<any>>
  @Input() displayColumns: Array<DisplayColumn> = new Array<DisplayColumn>()
  @Input() viewPageSize = 50
  @Input() tableName = 'DefaultTableName'
  @Input() overflow = {
    x: 'auto',
    y: 'auto',
  }
  @Input() multiselect = false
  @Input() multiselectActions: Array<{
    name: string
    callback: () => {}
    enabled: () => boolean
  }> = []
  @Input() searchDisabled = false
  @Input() pagination = true
  @Input() loading = false
  @Input() editable = false
  @Input() customFilter = false
  @Input() multiselectLimit = -1
  @Input() preserveSelectionsOnFilter = false
  @Input() highlightSelectedRow = false
  @Input() autoUpdate = true
  @Input() statuses
  @Input() heightOffset = '114'
  @Input() deleteElement = false
  @Input() showDeleteKeySystem = false

  @Output() onFilter = new EventEmitter<Array<any>>()
  @Output() rowClick = new EventEmitter<any>()
  @Output() rowDblClick = new EventEmitter<any>()
  @Output() selectedEntities = new EventEmitter<Array<any>>()
  @Output() editedEntity = new EventEmitter<any>()
  @Output() activeInputElement = new EventEmitter<any>()
  @Output() manualUpdate = new EventEmitter<{
    entity: any
    property: string
    value: any
  }>()
  @Output() keyupEnter = new EventEmitter<any>()
  @Output() highlightedEntity = new EventEmitter<any>()

  destroy = new Subject<boolean>()
  sortColumn: DisplayColumn
  getProperty = getProperty
  pageIndex = 0
  entities = []
  search$ = new Subject<void>()
  filteredEntities = []
  displayedEntities = []
  selected = []
  arrowIndex = -1

  constructor(private keyingStructureService: KeyingStructureService, private keyingSystemService: KeyingSystemService, private keyingDataSetsService: KeyingDataSetsService, private dialogService: DialogService, private loadingService: LoadingService, private errorService: ErrorService, private toastService: ToastService) {}

  ngOnInit() {
    this.sortColumn = this.displayColumns.find((displayColumn) => {
      return displayColumn.sortDirection > 0
    })
    this.search$
      .asObservable()
      .pipe(debounceTime(250), takeUntil(this.destroy))
      .subscribe((search) => {
        this.filter()
      })

    this.subject
      ?.asObservable()
      .pipe(takeUntil(this.destroy))
      .subscribe((entities) => {
        this.entities = entities
        if (!this.customFilter) {
          this.filter()
        } else {
          this.filteredEntities = this.entities
          this.selected = []
          this.selectedEntities.emit([])
          this.runSort()
          this.setDisplayEntities()
        }
      })
  }

  setFilteredEntity(filteredEntity, index) {
    if (this.multiselect) {
      if (!this.selected[index] && !this.canSelectEntities()) {
        return
      }
      if (this.multiselectLimit === 1) {
        for (let i = 0; i < this.filteredEntities.length; i++) {
          if (i !== index) {
            this.selected[i] = false
          }
        }
      }
      this.selected[index] = !this.selected[index]
      this.selectEntities()
    }
    if (this.highlightSelectedRow) {
      this.highlight(index)
    }
    this.rowClick.emit(filteredEntity)
  }

  highlight(index) {
    this.table.nativeElement.querySelectorAll('tbody tr').forEach((row) => {
      row.classList.remove('highlight')
    })
    var high: HTMLElement = this.table.nativeElement.querySelectorAll('tbody tr')[index]
    if (high) {
      this.highlightedEntity.emit(this.filteredEntities[index + this.viewPageSize * this.pageIndex])
      high.classList.toggle('highlight')
      high.scrollIntoView({
        block: 'end',
        inline: 'start',
      })
    } else {
      this.highlightedEntity.emit(null)
    }
  }

  ngOnDestroy() {
    this.paginator = null
    this.destroy.next(true)
    this.destroy.unsubscribe()
  }

  search() {
    this.search$.next()
  }

  sort(column, $event) {
    $event.stopPropagation()
    $event.preventDefault()
    $event.stopImmediatePropagation()
    if (!this.sortColumn) {
      this.sortColumn = column
    }
    if (this.sortColumn == column) {
      this.sortColumn.sortDirection = (this.sortColumn.sortDirection + 1) % 3
    } else {
      this.sortColumn.sortDirection = 0
      column.sortDirection = 1
    }
    this.sortColumn = column
    if (!this.customFilter) {
      this.filter()
    } else {
      this.runSort()
      this.selected = []
      this.selectedEntities.emit([])
      this.setDisplayEntities()
    }
    // this.coreSettings.save();
  }

  isActiveElement(element) {
    return element.getElementsByTagName('input')[0] == document.activeElement
  }

  resize(header: HTMLElement, index: number, event: MouseEvent) {
    var stop = function (e: MouseEvent) {
      e.stopImmediatePropagation()
      e.stopPropagation()
      e.preventDefault()
    }

    var updateWidth = (element: HTMLElement, value: number) => {
      if (value < 48) value = 48
      element.style.setProperty('width', `${value}px`, 'important')
      element.style.setProperty('min-width', `${value}px`, 'important')
      element.style.setProperty('max-width', `${value}px`, 'important')
      endWidth = `${value}px`
    }

    var move = (e: MouseEvent) => {
      endX = e.x - startX
      startX = e.x
      updateWidth(header, parseInt(window.getComputedStyle(header).width) + endX)
      stop(e)
    }

    var up = (e) => {
      var cssId = `${this.tableName}-CSS`
      var cssSelector = `#${this.tableName} th:nth-child(${
        index + 1 + (this.multiselect ? 1 : 0)
      }):not(:last-child), #${this.tableName} td:nth-child(${index + 1 + (this.multiselect ? 1 : 0)}):not(:last-child)`
      var css = document.getElementById(cssId) as HTMLStyleElement
      if (!css) {
        css = document.createElement('style') as HTMLStyleElement
        css.id = cssId
        css.type = 'text/css'
        document.head.appendChild(css)
      }
      var sheet = <CSSStyleSheet>css.sheet
      var rule = Array.from(sheet.cssRules).find((rule: CSSStyleRule) => rule.selectorText == cssSelector)
      if (rule) {
        sheet.deleteRule(Array.from(sheet.cssRules).indexOf(rule))
      }
      var ruleText = `${cssSelector} { width: ${endWidth}!important; min-width: ${endWidth}!important; max-width: ${endWidth}!important; }`
      sheet.insertRule(ruleText, sheet.cssRules.length)

      // if (this.coreSettings.styleSheets[cssId] == null)
      //   this.coreSettings.styleSheets[cssId] = {};

      // this.coreSettings.styleSheets[cssId][cssSelector] = ruleText;
      // this.coreSettings.save();

      header.style.width = header.style.minWidth = header.style.maxWidth = null
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
      stop(e)
    }

    var endWidth
    updateWidth(header, parseInt(window.getComputedStyle(header).width))

    var startX = event.x
    var endX = 0

    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
    stop(event)
  }

  filter() {
    this.selected = []
    if (!this.customFilter) {
      this.filteredEntities = this.entities.filter((opening) => {
        return every(
          this.displayColumns.filter((displayColumn) => {
            return displayColumn.searchText && displayColumn.searchText.length
          }),
          (displayColumn) => {
            return some(
              displayColumn.searchText.split('|').filter((s) => {
                return s.length
              }),
              (grouping) => {
                var searchArr = grouping.split('&').filter((searchValue) => {
                  return searchValue.length
                })
                return every(searchArr, (searchValue) => {
                  searchValue = searchValue.trim()
                  if (searchValue.startsWith('>')) {
                    return doesPassNumeric(opening, displayColumn.property, searchValue, true)
                  } else if (searchValue.startsWith('<')) {
                    return doesPassNumeric(opening, displayColumn.property, searchValue, false)
                  } else if (searchValue.startsWith('=')) {
                    return doesPass(opening, displayColumn.property, searchValue, true)
                  } else if (searchValue.startsWith('!')) {
                    return doesPass(opening, displayColumn.property, searchValue, true)
                  } else {
                    return doesPass(opening, displayColumn.property, searchValue, false)
                  }
                })
              }
            )
          }
        )
      })
    } else {
      this.filteredEntities = this.entities
    }

    if (this.pagination && this.paginator) {
      while (this.paginator.hasPreviousPage()) this.paginator.previousPage()
    }

    this.runSort()

    this.selectedEntities.emit([])
    this.onFilter.next(this.filteredEntities)
    this.setDisplayEntities()
  }

  runSort() {
    this.arrowIndex = -1
    this.highlight(this.arrowIndex)
    if (this.sortColumn && this.sortColumn.sortDirection > 0) {
      var collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      })
      this.filteredEntities = this.filteredEntities.sort((a, b) => {
        var aValue = getProperty(a, this.sortColumn.property)
        var bValue = getProperty(b, this.sortColumn.property)
        return collator.compare(aValue ? aValue.toString() : null, bValue ? bValue.toString() : null)
      })
      if (this.sortColumn.sortDirection == 2) this.filteredEntities = this.filteredEntities.reverse()
    }
  }

  setDisplayEntities() {
    this.displayedEntities = this.pagination
      ? this.filteredEntities.slice(this.pageIndex * this.viewPageSize, this.viewPageSize * (this.pageIndex + 1))
      : this.filteredEntities
  }

  canSelectEntities() {
    return this.multiselectLimit < 2
      ? true
      : this.selected.filter((s) => {
          return s
        }).length < this.multiselectLimit
  }

  selectEntities() {
    this.selectedEntities.emit(
      this.filteredEntities.filter((filteredEntity, index) => {
        return this.selected[index]
      })
    )
  }

  editEntity(entity, property, value) {
    if (!this.autoUpdate) {
      return this.manualUpdate.emit({ entity, property, value })
    }
    if (getProperty(entity, property) != value) {
      updateProperty(
        entity,
        property,
        this.displayColumns.find((displayColumn) => {
          return displayColumn.property == property
        }).type == 'number'
          ? parseFloat(value)
          : value
      )
      this.editedEntity.emit(entity)
    }
  }

  dragOverElement: HTMLElement
  moveColumn(index: number, $event: DragEvent) {
    var tableHeaderArray = this.tableHeaders.toArray().map((tableHeader) => {
      return tableHeader.nativeElement
    })
    tableHeaderArray.forEach((tableHeader) => {
      tableHeader.classList.remove('dragover')
    })
    if (this.dragOverElement) {
      var moveIndex = tableHeaderArray.indexOf(this.dragOverElement)
      var eventDisplayColumn = this.displayColumns[index]
      this.displayColumns.splice(index, 1)
      this.displayColumns.splice(moveIndex, 0, eventDisplayColumn)
    }
    $event.dataTransfer.clearData()
    // this.coreSettings.save();
  }

  handleArrows($event: KeyboardEvent) {
    if ($event.keyCode == 40) {
      //down
      if (this.arrowIndex + 1 < this.filteredEntities.length) {
        this.highlight(++this.arrowIndex)
      }
    } else if ($event.keyCode == 38) {
      //up
      if (this.arrowIndex - 1 > -1) {
        this.highlight(--this.arrowIndex)
      }
    }
  }

    
  /* istanbul ignore next */
  deleteKeySystem(keySystem: KeySystem) {

    // Verify whether the system is assigned to the logged-in user according to business rules before proceeding.
    if (!this.deleteElement) {
      this.toastService.showToast({ title: 'Error', subTitle: 'Cannot delete key system: User does not own the lock', type: 'error' })
      return
    }
      const confirmData = {
        title: 'Delete Key System',
        message: `Are you sure you want to delete this key system: ${keySystem.topMasterKeyName}?`,
        cancel: 'Cancel',
        confirm: 'Delete',
        showCancel: true,
        reasonRequired: true,
      }

      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, confirmData, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          const auditRequestFormData = { auditTrailRequestDto: {
            reasonCode: result.auditTrailRequest.reasonCode,
            description: result.auditTrailRequest.description,
          }}
          
        this.loadingService.set({ isLoading: true, message: 'Deleting Key System' })
          
          this.keyingSystemService.deleteKeySystem(keySystem.structureId, keySystem.id, auditRequestFormData).pipe(take(1)).subscribe(() => {

          this.keyingStructureService.structureId$.pipe(take(1)).subscribe((structureId) => {
                this.keyingStructureService
            .getStructure(structureId)
            .pipe(take(1))
            .subscribe((structure) => {
              if (!structure) {
                return
              }
              this.keyingStructureService.structure.next(structure)
              this.keyingStructureService
                .getStructureKeySystems(structure.id)
                .pipe(take(1))
                .subscribe((keySystems) => {
                  keySystems.forEach((keySystem) => {
                    if (keySystem.keySectionId) {
                      this.keyingDataSetsService.setKeySectionName(structure, keySystem)
                    }
                  })
                    this.filteredEntities = keySystems
                    this.setDisplayEntities()
                })
            })
          })
            this.loadingService.set({ isLoading: false, message: '' })
          },
        (error) => {
          this.errorService.throw(error)
          this.loadingService.set({ isLoading: false, message: '' })
        })
        }
      })
  }
}

export class DisplayColumn {
  property: string
  type: string
  title: string
  sortDirection: number
  searchText: string
  default
  defaultOrder: number
  editable
  required
  createOrder: number
  defaultValue: any
  options: Array<any>
  textArea

  snippet? = false
  snippetType?: number = -1

  constructor(obj: any) {
    for (var property in this) {
      this[property] = this[property] == null ? obj[property] : this[property]
    }
  }
}
